import { useEffect } from 'react';
import { graphql } from 'react-relay';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { toAri, getAriConfig, type AriConfig } from '@atlassian/jira-platform-ari';
import { useFetchQuery } from '@atlassian/jira-relay-utils/src/services/use-fetch-query/index.tsx';
import type { services_jira_projectOpsGenieAssociationV2_associatedOpsgenieTeamQueryAgg_Query$data as Response } from '@atlassian/jira-relay/src/__generated__/services_jira_projectOpsGenieAssociationV2_associatedOpsgenieTeamQueryAgg_Query.graphql';

export type TeamAssociation = {
	opsgenieTeamId: string;
	name?: string;
	opsgenieTeamAri?: string;
};

export const getTeamIdFromAri = (opsgenieTeamAri: string) => {
	let teamId = opsgenieTeamAri;
	const ari = toAri(opsgenieTeamAri);
	if (ari) {
		const teamAri: AriConfig = getAriConfig(ari);
		teamId = teamAri.resourceId;
	}
	return teamId;
};

export const toTeamAssociation = (associatedOpsgenieTeamQuery: Response): TeamAssociation | null =>
	(associatedOpsgenieTeamQuery.devOps?.ariGraph?.teamRelationships?.edges &&
		associatedOpsgenieTeamQuery.devOps?.ariGraph?.teamRelationships?.edges.length > 0 &&
		associatedOpsgenieTeamQuery.devOps?.ariGraph?.teamRelationships?.edges
			.filter(Boolean)
			.map((edge) => edge?.node.to.opsgenieTeam)
			.flatMap((team) =>
				team && team.id
					? {
							opsgenieTeamId: getTeamIdFromAri(team.id),
							opsgenieTeamAri: team.id,
							name: team.name || '',
						}
					: [],
			)[0]) ||
	null;

export const buildJiraProjectAri = (cloudId: string, projectId: string) =>
	`ari:cloud:jira:${cloudId}:project/${projectId}`;

export const useAssociatedOpsgenieTeamQueryAGG = (projectAri: string | typeof undefined) => {
	const { data, loading, error } = useFetchQuery(
		graphql`
			query services_jira_projectOpsGenieAssociationV2_associatedOpsgenieTeamQueryAgg_Query(
				$jiraProjectId: ID!
			) {
				devOps {
					ariGraph {
						teamRelationships: relationships(
							type: "project-associated-opsgenie-team"
							from: $jiraProjectId
							first: 5
						) {
							edges {
								node {
									to {
										opsgenieTeam: data {
											... on OpsgenieTeam {
												id
												name
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			jiraProjectId: projectAri || '',
		},
		{
			skip: !projectAri,
		},
	);

	useEffect(() => {
		if (error) {
			log.safeErrorWithoutCustomerData(
				'devops.project-opsgenie-association.query-association',
				`Retrieving project opsgenie association AGG contains errors: ${JSON.stringify(error)}`,
			);
			fireErrorAnalytics({
				error,
				meta: {
					id: 'graphqlQuery',
					packageName: 'jiraProjectOpsgenieUseAssociatedOpsgenieTeam',
				},
			});
		}
	}, [error]);

	// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'services_jira_projectOpsGenieAssociationV2_associatedOpsgenieTeamQueryAgg_Query$data'.
	return { data: data && toTeamAssociation(data), loading, error };
};
